import { useCallback, useMemo } from "react";

import {
  faFile,
  faPlus,
  faSpinner,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { Column, Row } from "@tanstack/react-table";
import { AxiosError } from "axios";
import { twJoin } from "tailwind-merge";

import { useFile } from "src/api/filesApi/queries";
import { DatasetRow } from "src/api/types";
import { Button } from "src/base-components/Button";
import { getFileName } from "src/base-components/FilePill";
import {
  useFileUpload,
  useFileUploadDropzone,
} from "src/base-components/FileUpload";
import { Pill } from "src/base-components/Pill";
import { GetFileEnvFilesFileIdGetEnvEnum } from "src/clients/files";
import { CellWrapper } from "src/datasets/components/CellWrapper";
import { RowUpdateFunction } from "src/datasets/DatasetTable/DatasetEditTable";
import { JSONValue } from "src/datasets/DatasetTable/types";
import { Icon as DSIcon, IconButton } from "src/design-system/Icon";
import { FileViewer } from "src/files/FileViewer";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  DEFAULT_ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE_BYTES,
} from "src/utils/files";

type FileCellProps = {
  disabled: boolean;
  propValue: JSONValue | undefined;
  column: Column<DatasetRow>;
  row: Row<DatasetRow>;
  warning: boolean;
  selected: boolean;
  onChange: RowUpdateFunction;
};

export const FileCell: React.FC<FileCellProps> = ({
  disabled,
  propValue,
  column,
  row,
  warning,
  selected,
  onChange,
}) => {
  const fileIds: string[] = useMemo(
    () => (Array.isArray(propValue) ? propValue.filter(isString) : []),
    [propValue],
  );

  const handleChange = useCallback(
    (value: string | string[] | null) => {
      onChange(row, column.id, value ?? null);
    },
    [onChange, row, column.id],
  );

  const handleRemove = useCallback(
    (idToRemove: string) => {
      const updated = fileIds.filter((id) => id !== idToRemove);
      onChange(row, column.id, updated.length > 0 ? updated : null);
    },
    [fileIds, onChange, row, column.id],
  );

  const { fileUploadProps } = useFileUpload({
    multiple: true,
    env: "sandbox",
    source: "dataset-upload",
    value: fileIds.length > 0 ? fileIds : undefined,
    onChange: handleChange,
    disabled,
  });

  const { getRootProps, getInputProps, isDragActive, open } =
    useFileUploadDropzone({
      onFileSelect: fileUploadProps.onFileSelect,
      multiple: true,
      disabled,
      accept: DEFAULT_ACCEPTED_FILE_TYPES,
      maxSize: MAX_FILE_SIZE_BYTES,
    });

  const isUploading =
    fileUploadProps.uploadingFiles && fileUploadProps.uploadingFiles.length > 0;

  return (
    <div
      {...getRootProps({
        className: twJoin("h-full w-full", isDragActive && "bg-indigo-50"),
        // Prevent the dropzone from stealing click (cell selection handled by td)
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      })}
    >
      <input {...getInputProps()} />
      <CellWrapper
        disabled={disabled}
        editing={false}
        selected={selected}
        warning={warning}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 py-1.5">
          {fileIds.map((id) => (
            <DatasetFilePill
              key={id}
              disabled={disabled}
              fileId={id}
              onRemove={() => handleRemove(id)}
            />
          ))}
          {isUploading && (
            <div className="flex items-center gap-1">
              <DSIcon icon={faSpinner} size="2xs" variant="active" spin />
              <span className="font-inter-normal-12px text-gray-500">
                Uploading...
              </span>
            </div>
          )}
          {!disabled && <UploadButton disabled={disabled} onClick={open} />}
        </div>
      </CellWrapper>
    </div>
  );
};

const UploadButton: React.FC<{
  disabled: boolean;
  onClick: () => void;
}> = ({ disabled, onClick }) => (
  <Button
    disabled={disabled}
    iconLeft={faPlus}
    size="sm"
    variant="secondary"
    onClick={onClick}
  >
    Upload file(s)
  </Button>
);

const DatasetFilePill: React.FC<{
  fileId: string;
  disabled: boolean;
  onRemove: () => void;
}> = ({ fileId, disabled, onRemove }) => {
  const { workspace } = useWorkspaceContext();
  const {
    data: file,
    isLoading,
    error,
  } = useFile(
    fileId,
    "sandbox" as GetFileEnvFilesFileIdGetEnvEnum,
    workspace.base_url!,
  );

  const is404 = error instanceof AxiosError && error.response?.status === 404;
  const is403 = error instanceof AxiosError && error.response?.status === 403;

  const getDisplayFileName = () => {
    if (isLoading) return "Loading file...";
    if (is403) return "File access denied";
    if (is404 || !file?.data.data?.attributes) return "File not found";
    if (error) return "Error fetching file";
    return getFileName(file.data.data);
  };
  const fileName = getDisplayFileName();
  const variant = error && !is404 ? "red" : "gray";

  const pill = (
    <Pill size="sm" variant={variant}>
      <Pill.Icon icon={faFile} />
      <Pill.Text>{fileName}</Pill.Text>
      {!disabled && (
        <IconButton
          icon={faTimes}
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        />
      )}
    </Pill>
  );

  if (!isLoading && !error && file?.data.data?.attributes) {
    return (
      <div className="inline-block">
        <FileViewer file={file.data.data.attributes}>{pill}</FileViewer>
      </div>
    );
  }

  return <div className="inline-block">{pill}</div>;
};

const isString = (v: unknown): v is string => typeof v === "string";
